import isEqual from 'react-fast-compare';

/**
 * Deterministic counting for the work the table performs.
 *
 * Wall-clock timings are too noisy to assert on in CI, but the number of times the table renders a
 * component, recomputes a selector or walks a deep comparison in response to a given interaction is
 * deterministic, so it can be asserted exactly. The work-count suite in `core-ui/__tests__/perf/`
 * turns counting on, drives a scenario, and compares the counts against committed baselines.
 *
 * Renders alone do not explain why a render happened, and some of the most expensive work the table
 * does is invisible to them: a deep compare that walks the whole cell matrix costs no renders at
 * all. Each counter family below exists so that a change meant to remove one of those costs has a
 * metric that proves it.
 *
 * Counting is off unless a test turns it on, so each instrumented site pays for one comparison
 * against a module-level null.
 */

/**
 * The components whose renders are counted. Anything that renders once per cell or per row belongs
 * here; the whole point is to notice when a single-cell interaction re-renders the whole table.
 */
export const INSTRUMENTED_TABLE_COMPONENTS = [
  'TableEditableClass',
  'TableEditableComponent',
  'TableRow',
  'TableCell',
  'TableAxisCell',
  'TableHeaderRow',
  'TableCellEditor',
] as const;

export type InstrumentedTableComponent = (typeof INSTRUMENTED_TABLE_COMPONENTS)[number];

export type TableRenderCounts = Record<InstrumentedTableComponent, number>;

/**
 * What a selector cost. Calls are deliberately not counted: a memoized selector called again with
 * the same store returns from reselect's argument memoization without running its inputs, so a call
 * count measures how many places read a value rather than how much work happened. It moves with
 * almost any refactor while the work stays the same, which would make these baselines churn for
 * reasons nobody should have to read a diff about.
 */
export interface SelectorCounts {
  /** How many times the selector's combiner ran because its inputs had changed. */
  recomputations: number;
  /** How many times the selector compared a fresh result against the previous one. */
  resultEqualityChecks: number;
}

/**
 * The deep comparisons hot enough to be worth counting. Each one can walk the entire cell matrix on
 * a single call, so the node counts matter more than the call counts.
 */
export const INSTRUMENTED_DEEP_COMPARE_SITES = [
  'TableCellMemo',
  'TableHeaderRowMemo',
  'VolatileTableProps',
] as const;

export type InstrumentedDeepCompareSite = (typeof INSTRUMENTED_DEEP_COMPARE_SITES)[number];

export interface DeepCompareCounts {
  /** How many times the comparison ran. */
  calls: number;
  /**
   * How many values the comparison visited in total, counting both sides of every pair it reached.
   * A comparison that exits at the first differing key visits a handful of nodes; one that proves
   * two cell matrices equal visits every cell.
   */
  nodesVisited: number;
}

/**
 * Everything counted during one measurement phase. The shape grows one family at a time as each
 * metric lands; every family is keyed so the work-count diff can report which counter moved.
 */
export interface TableInstrumentationCounts {
  renders: TableRenderCounts;
  /** Keyed by the name each selector is created with, and holding only selectors that ran. */
  selectors: Record<string, SelectorCounts>;
  deepCompares: Record<InstrumentedDeepCompareSite, DeepCompareCounts>;
}

interface Counters {
  renders: TableRenderCounts;
  selectors: Record<string, SelectorCounts>;
  deepCompares: Record<InstrumentedDeepCompareSite, DeepCompareCounts>;
}

function zeroedCounters(): Counters {
  const renders = {} as TableRenderCounts;
  for (const component of INSTRUMENTED_TABLE_COMPONENTS) {
    renders[component] = 0;
  }
  const deepCompares = {} as Record<InstrumentedDeepCompareSite, DeepCompareCounts>;
  for (const site of INSTRUMENTED_DEEP_COMPARE_SITES) {
    deepCompares[site] = {calls: 0, nodesVisited: 0};
  }
  return {renders, selectors: {}, deepCompares};
}

let counters: Counters | null = null;

/**
 * Whether a scenario is counting right now.
 *
 * The wrapping sites (each named selector, and the counted deep-comparing selector) consult this
 * when they are built, so a table built by any other test is left uninstrumented: it cannot be
 * counted, and it cannot add to another scenario's numbers. A scenario therefore has to start
 * counting before it mounts the table it means to measure, which `measureTableWork` does.
 */
export function isTableCountingActive(): boolean {
  return counters != null;
}

/** Begin counting table work, discarding any counts from a previous scenario. */
export function startCountingTableWork(): void {
  counters = zeroedCounters();
}

/** Stop counting table work. Safe to call when counting was never started. */
export function stopCountingTableWork(): void {
  counters = null;
}

/**
 * Zero the counters while leaving counting on, so a scenario can measure an interaction without the
 * mount that preceded it.
 */
export function resetTableCounts(): void {
  if (counters != null) {
    counters = zeroedCounters();
  }
}

export function readTableCounts(): TableInstrumentationCounts {
  if (counters == null) {
    throw new Error('Table counting is not active. Call startCountingTableWork() first.');
  }
  return {
    renders: {...counters.renders},
    selectors: Object.fromEntries(
      Object.entries(counters.selectors).map(([name, selectorCounts]) => [name, {...selectorCounts}])
    ),
    deepCompares: Object.fromEntries(
      Object.entries(counters.deepCompares).map(([site, siteCounts]) => [site, {...siteCounts}])
    ) as Record<InstrumentedDeepCompareSite, DeepCompareCounts>,
  };
}

/**
 * Record one render of `component`. Called from the component's render body, so it runs once per
 * render attempt regardless of whether React commits the result.
 */
export function countTableRender(component: InstrumentedTableComponent): void {
  if (counters != null) {
    counters.renders[component] += 1;
  }
}

function selectorCounts(name: string): SelectorCounts | null {
  if (counters == null) {
    return null;
  }
  const existing = counters.selectors[name];
  if (existing != null) {
    return existing;
  }
  const created = {recomputations: 0, resultEqualityChecks: 0};
  counters.selectors[name] = created;
  return created;
}

/** Record one run of the named selector's combiner. */
export function countSelectorRecomputation(name: string): void {
  const selector = selectorCounts(name);
  if (selector != null) {
    selector.recomputations += 1;
  }
}

/** Record one comparison of the named selector's fresh result against its previous one. */
export function countSelectorResultEqualityCheck(name: string): void {
  const selector = selectorCounts(name);
  if (selector != null) {
    selector.resultEqualityChecks += 1;
  }
}

/** What one counted walk found: how many values it reached, and whether the two sides matched. */
interface WalkResult {
  nodes: number;
  equal: boolean;
}

/**
 * Values `react-fast-compare` compares by their own semantics instead of walking key by key: dates,
 * regexps, maps, sets, typed arrays, and React elements, whose `_owner` is a cyclic graph the
 * comparator deliberately skips. The walk treats each as a single value, which keeps it finite and
 * keeps it from reporting traversal the comparison never performs.
 */
function isComparedWhole(value: object): boolean {
  return (
    '$$typeof' in value ||
    value instanceof Date ||
    value instanceof RegExp ||
    value instanceof Map ||
    value instanceof Set ||
    ArrayBuffer.isView(value)
  );
}

/**
 * Walk `a` and `b` the way the deep comparison does, counting every value reached on either side
 * and stopping at the first difference.
 *
 * `seen` holds the pairs currently being walked. The comparison itself does not survive a value
 * that contains itself, so this only guarantees that the counting cannot be the thing that hangs.
 */
function walkComparison(a: unknown, b: unknown, seen: Map<object, Set<object>>): WalkResult {
  if (a === b) {
    return {nodes: 2, equal: true};
  }
  if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) {
    return {nodes: 2, equal: false};
  }
  if (Array.isArray(a) !== Array.isArray(b) || isComparedWhole(a) || isComparedWhole(b)) {
    return {nodes: 2, equal: isEqual(a, b)};
  }

  const inProgress = seen.get(a);
  if (inProgress?.has(b) === true) {
    // Already being walked further up the stack. Counting it again would not terminate.
    return {nodes: 0, equal: true};
  }
  if (inProgress == null) {
    seen.set(a, new Set([b]));
  } else {
    inProgress.add(b);
  }

  try {
    const aKeys = Array.isArray(a) ? a.map((_value, index) => String(index)) : Object.keys(a);
    const bKeyCount = Array.isArray(b) ? b.length : Object.keys(b).length;
    if (aKeys.length !== bKeyCount) {
      return {nodes: 2, equal: false};
    }
    let nodes = 2;
    for (const key of aKeys) {
      if (!Object.hasOwn(b, key)) {
        return {nodes, equal: false};
      }
      const child = walkComparison(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
        seen
      );
      nodes += child.nodes;
      if (!child.equal) {
        return {nodes, equal: false};
      }
    }
    return {nodes, equal: true};
  } finally {
    seen.get(a)?.delete(b);
  }
}

function countedIsEqualImpl(site: InstrumentedDeepCompareSite, a: unknown, b: unknown): boolean {
  const result = isEqual(a, b);
  if (counters != null) {
    const siteCounts = counters.deepCompares[site];
    siteCounts.calls += 1;
    siteCounts.nodesVisited += walkComparison(a, b, new Map()).nodes;
  }
  return result;
}

/**
 * Build the deep comparison used at `site`, which records each call and how many values it reached.
 *
 * The answer always comes from `react-fast-compare` itself, so an instrumented site cannot decide
 * equality differently from an uninstrumented one; the walk alongside it only supplies the count.
 * Outside a counting scenario this pays for one comparison against a module-level null per call.
 */
export function makeCountedIsEqual(site: InstrumentedDeepCompareSite): (a: unknown, b: unknown) => boolean {
  return (a, b) => countedIsEqualImpl(site, a, b);
}
